// src/features/services/sales/hooks/useSalesOrders.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchSalesOrdersApi,
  fetchSkusApi,
  createSalesOrderApi,
  fetchPipelineStatusApi,
  type SalesOrderListResponse,
  type Sku,
  type CreateSalesOrderPayload,
  type CreateSalesOrderResponse,
  type PipelineStatus,
} from '../api/salesOrderApi';

const QK_ORDERS = ['sales', 'orders'] as const;
const QK_SKUS   = ['sales', 'skus']   as const;

export function useSalesOrders(params?: { status?: string; limit?: number }) {
  const qc = useQueryClient();

  const list = useQuery<SalesOrderListResponse, Error>({
    queryKey: [...QK_ORDERS, params],
    queryFn: () => fetchSalesOrdersApi(params),
    staleTime: 30_000,
  });

  const skus = useQuery<{ items: Sku[] }, Error>({
    queryKey: [...QK_SKUS],
    queryFn: () => fetchSkusApi(),
    staleTime: 120_000,
  });

  const create = useMutation<CreateSalesOrderResponse, Error, CreateSalesOrderPayload>({
    mutationFn: (p) => createSalesOrderApi(p),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...QK_ORDERS] }),
  });

  return { ...list, skus, create };
}

export function usePipelineStatus(serialNumber: string | null) {
  return useQuery<PipelineStatus, Error>({
    queryKey: ['sales', 'pipeline', serialNumber],
    queryFn: () => fetchPipelineStatusApi(serialNumber!),
    enabled: !!serialNumber,
    staleTime: 10_000,
    refetchInterval: 10_000,
  });
}
